import React, { useEffect, useState, useRef, Component } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { About } from './components/About';
import { Portfolio } from './components/Portfolio';
import { Disciplines } from './components/Disciplines';
import { HallOfFame } from './components/HallOfFame';
import { MediaFeatures } from './components/MediaFeatures';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { CustomCursor } from './components/CustomCursor';
import { Fireworks } from './components/Fireworks';
import { AvatarSelection } from './components/AvatarSelection';
import { AvatarIndicator } from './components/AvatarIndicator';
import { AvatarProvider, useAvatar } from './context/AvatarContext';
import { Shop } from './components/Shop';
import { EnhancedLoadingScreen } from './components/EnhancedLoadingScreen';
import { IntroScreen } from './components/IntroScreen';
import { Companies } from './components/Companies';
import { TeamMembers } from './components/TeamMembers';
import { DonationBanner } from './components/DonationBanner';
import { SoundEffects, SoundProvider, useSound } from './components/SoundEffects';
import { VolumeIcon, Volume2Icon, PauseIcon, PlayIcon, MonitorIcon, ZapIcon, InfoIcon, SparklesIcon } from 'lucide-react';
import { TechnicalOverlay } from './components/TechnicalOverlay';
import { ThemeProvider, useTheme } from './context/ThemeContext';
// Main App wrapper with context providers
export function App() {
  return <ThemeProvider data-id="element-0">
      <SoundProvider data-id="element-1">
        <AvatarProvider data-id="element-2">
          <AppContent data-id="element-3" />
        </AvatarProvider>
      </SoundProvider>
    </ThemeProvider>;
}
// Main application content
function AppContent() {
  const [showIntro, setShowIntro] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({
    x: 0,
    y: 0
  });
  const [cursorType, setCursorType] = useState('default');
  const [isMobile, setIsMobile] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showTechnicalOverlay, setShowTechnicalOverlay] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [fireworksIntensity, setFireworksIntensity] = useState(1);
  const [fireworksColor, setFireworksColor] = useState('default');
  const {
    avatar,
    setAvatar,
    showAvatarSelection,
    setShowAvatarSelection
  } = useAvatar();
  const {
    playSound,
    isMuted,
    toggleMute,
    volume,
    setVolume
  } = useSound();
  const {
    theme,
    isDark
  } = useTheme();
  const audioRef = useRef(null);
  // Handle intro screen completion
  const handleStartExperience = () => {
    playSound('transition');
    setShowIntro(false);
  };
  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Initial check
    checkMobile();
    // Optimized cursor position tracking with throttling
    let lastUpdateTime = 0;
    const THROTTLE_MS = 10; // Throttle to every 10ms for smoother performance
    const updateCursorPosition = e => {
      const now = Date.now();
      if (now - lastUpdateTime > THROTTLE_MS) {
        setCursorPosition({
          x: e.clientX,
          y: e.clientY
        });
        lastUpdateTime = now;
      }
    };
    // Add event listeners
    window.addEventListener('mousemove', updateCursorPosition, {
      passive: true
    });
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('mousemove', updateCursorPosition);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  // Handle cursor context for interactive elements
  const handleCursorChange = type => {
    if (type !== cursorType) {
      if (type === 'button') {
        playSound('hover');
      }
      setCursorType(type);
    }
  };
  // Audio controls
  const toggleAudio = () => {
    playSound('click');
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.error('Audio playback failed:', e));
      }
      setAudioPlaying(!audioPlaying);
    }
  };
  const handleVolumeChange = e => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };
  const toggleTechnicalOverlay = () => {
    playSound('click');
    setShowTechnicalOverlay(!showTechnicalOverlay);
  };
  const toggleFireworks = () => {
    playSound('click');
    setShowFireworks(!showFireworks);
    // Cycle through colors when toggling
    if (!showFireworks) {
      const colors = ['default', 'blue', 'purple', 'teal', 'cyberpunk'];
      const currentIndex = colors.indexOf(fireworksColor);
      const nextIndex = (currentIndex + 1) % colors.length;
      setFireworksColor(colors[nextIndex]);
    }
  };
  const increaseFireworksIntensity = () => {
    playSound('click');
    setFireworksIntensity(prev => Math.min(prev + 0.5, 3));
  };
  // Handle avatar selection
  const handleAvatarSelect = selectedAvatar => {
    playSound('success');
    setAvatar(selectedAvatar);
    setShowAvatarSelection(false);
    // Show fireworks briefly to celebrate selection
    setFireworksColor(selectedAvatar.id === 'quantum' ? 'purple' : selectedAvatar.id === 'ethereal' ? 'teal' : selectedAvatar.id === 'neural' ? 'blue' : 'cyberpunk');
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 3000);
  };
  // Get avatar-specific fireworks color
  const getFireworksColor = () => {
    if (!avatar) return fireworksColor;
    switch (avatar.id) {
      case 'neural':
        return 'blue';
      case 'synthetic':
        return 'purple';
      case 'hybrid':
        return 'red';
      case 'ethereal':
        return 'gold';
      case 'viral':
        return 'green';
      case 'quantum':
        return 'purple';
      default:
        return fireworksColor;
    }
  };
  // Handle loading completion
  const handleLoadingComplete = () => {
    playSound('success');
    setIsLoading(false);
    // Show fireworks briefly on load
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 5000);
  };
  return <div className="w-full min-h-screen bg-[#030508] text-[#f0f6ff] font-sans cursor-none relative" style={avatar ? {
    '--primary': avatar.accent,
    '--primary-dark': avatar.secondaryAccent,
    '--secondary': avatar.id === 'synthetic' ? '#6ff8f0' : '#e64cff'
  } : {}} data-id="element-4">
      <style jsx global data-id="element-5">{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Syne:wght@400;500;600;700&family=JetBrains+Mono:wght@300;400;500&display=swap');
        :root {
          /* Dark theme (default) */
          --primary: #6ff8f0;
          --primary-dark: #2a4d7f;
          --secondary: #e64cff;
          --secondary-dark: #8a2be2;
          --accent: #ff6b6b;
          --text: #f0f6ff;
          --text-secondary: #a0c0e0;
          --bg-primary: #030508;
          --bg-secondary: #0a1018;
          --border: #1a2a40;
          --border-light: #243240;
        }
        /* Light theme variables */
        [data-theme='light'] {
          --primary: #0095c8;
          --primary-dark: #006b9c;
          --secondary: #c40099;
          --secondary-dark: #8e006e;
          --accent: #ff6b6b;
          --text: #1a2a40;
          --text-secondary: #3a5a80;
          --bg-primary: #f0f6ff;
          --bg-secondary: #e0eaf9;
          --border: #cad5e8;
          --border-light: #d8e0f0;
        }
        body {
          font-family: 'Space Grotesk', sans-serif;
          background-color: var(--bg-primary);
          color: var(--text);
          cursor: none;
          overflow-x: hidden;
          line-height: 1.4;
          letter-spacing: -0.01em;
        }
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-family: 'Syne', sans-serif;
          letter-spacing: -0.03em;
          line-height: 1.1;
        }
        p {
          margin-bottom: 0.75rem;
        }
        .font-mono {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85rem;
        }
        .font-heading {
          font-family: 'Syne', sans-serif;
        }
        .font-sans {
          font-family: 'Space Grotesk', sans-serif;
        }
        /* Glassmorphism */
        .glass {
          background: rgba(var(--bg-primary-rgb, 3, 5, 8), 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--primary-rgb, 111, 248, 240), 0.1);
        }
        [data-theme='light'] .glass {
          background: rgba(240, 246, 255, 0.7);
          border: 1px solid rgba(0, 149, 200, 0.1);
        }
        /* Modern Animations */
        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        @keyframes pulse-subtle {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 3s infinite ease-in-out;
        }
        @keyframes float-subtle {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        .animate-float {
          animation: float-subtle 4s infinite ease-in-out;
        }
        /* Gradient Text */
        .text-gradient {
          background: linear-gradient(
            to right,
            var(--primary),
            var(--primary-dark)
          );
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-shadow: 0 0 15px rgba(var(--primary-rgb, 111, 248, 240), 0.3);
        }
        .text-gradient-purple {
          background: linear-gradient(
            to right,
            var(--secondary),
            var(--secondary-dark)
          );
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-shadow: 0 0 15px rgba(var(--secondary-rgb, 230, 76, 255), 0.3);
        }
        /* Hide default cursor */
        * {
          cursor: none !important;
        }
        /* Performance optimization */
        .hardware-accelerated {
          transform: translateZ(0);
          backface-visibility: hidden;
          perspective: 1000px;
          will-change: transform, opacity;
        }
        /* Audio Player */
        .audio-player {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 100;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 50px;
          background: rgba(var(--bg-primary-rgb, 3, 5, 8), 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          box-shadow: 0 0 15px rgba(var(--primary-rgb, 111, 248, 240), 0.15);
          transition: all 0.3s ease;
        }
        .audio-player:hover {
          box-shadow: 0 0 20px rgba(var(--primary-rgb, 111, 248, 240), 0.3);
        }
        .audio-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.1);
          color: var(--primary);
          border: none;
          transition: all 0.3s ease;
        }
        .audio-button:hover {
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          transform: scale(1.05);
        }
        .volume-slider {
          -webkit-appearance: none;
          width: 60px;
          height: 4px;
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          border-radius: 2px;
          outline: none;
        }
        .volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--primary);
          cursor: pointer;
          box-shadow: 0 0 8px rgba(var(--primary-rgb, 111, 248, 240), 0.5);
        }
        .volume-slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--primary);
          cursor: pointer;
          box-shadow: 0 0 8px rgba(var(--primary-rgb, 111, 248, 240), 0.5);
          border: none;
        }
        /* System status indicator */
        .system-status {
          position: fixed;
          top: 80px;
          left: 20px;
          z-index: 100;
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px;
          border-radius: 4px;
          background: rgba(var(--bg-primary-rgb, 3, 5, 8), 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: var(--primary);
          transform: translateX(-200%);
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .system-status.active {
          transform: translateX(0);
        }
        .system-status-item {
          display: flex;
          justify-content: space-between;
          gap: 12px;
        }
        .system-status-value {
          color: var(--secondary);
        }
        /* Loading animation */
        @keyframes loader {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }
        /* Technical overlay toggle */
        .tech-overlay-toggle {
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 100;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(var(--bg-primary-rgb, 3, 5, 8), 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          box-shadow: 0 0 15px rgba(var(--primary-rgb, 111, 248, 240), 0.15);
          transition: all 0.3s ease;
        }
        .tech-overlay-toggle:hover {
          box-shadow: 0 0 20px rgba(var(--primary-rgb, 111, 248, 240), 0.3);
        }
        /* Fireworks controls */
        .fireworks-controls {
          position: fixed;
          bottom: 20px;
          left: 20px;
          z-index: 100;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 50px;
          background: rgba(var(--bg-primary-rgb, 3, 5, 8), 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          box-shadow: 0 0 15px rgba(var(--primary-rgb, 111, 248, 240), 0.15);
          transition: all 0.3s ease;
        }
        .fireworks-controls:hover {
          box-shadow: 0 0 20px rgba(var(--primary-rgb, 111, 248, 240), 0.3);
        }
        .fireworks-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.1);
          color: var(--primary);
          border: none;
          transition: all 0.3s ease;
        }
        .fireworks-button:hover {
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.2);
          transform: scale(1.05);
        }
        .fireworks-button.active {
          background: rgba(var(--secondary-rgb, 230, 76, 255), 0.2);
          color: var(--secondary);
          animation: pulse-glow 2s infinite;
        }
        @keyframes pulse-glow {
          0%,
          100% {
            box-shadow: 0 0 5px rgba(var(--secondary-rgb, 230, 76, 255), 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(var(--secondary-rgb, 230, 76, 255), 0.8);
          }
        }
        .intensity-indicator {
          display: flex;
          gap: 2px;
        }
        .intensity-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(var(--primary-rgb, 111, 248, 240), 0.3);
        }
        .intensity-dot.active {
          background: var(--primary);
        }
        @media (max-width: 768px) {
          * {
            cursor: auto !important;
          }
          .audio-player {
            bottom: 10px;
            right: 10px;
          }
          .fireworks-controls {
            bottom: 10px;
            left: 10px;
          }
          .system-status {
            top: 70px;
            left: 10px;
          }
          .tech-overlay-toggle {
            top: 10px;
            left: 10px;
          }
        }
      `}</style>

      {/* Donation Banner */}
      <DonationBanner onCursorChange={handleCursorChange} data-id="element-6" />

      {/* Sound Effects Component */}
      <SoundEffects data-id="element-7" />

      {/* Fireworks effect */}
      <Fireworks isActive={showFireworks} intensity={avatar ? getFireworksColor() : fireworksColor} data-id="element-8" />

      {/* Audio element */}
      <audio ref={audioRef} loop preload="auto" src="https://assets.codepen.io/308367/ambient-space-track.mp3" data-id="element-9" />

      {/* Technical overlay toggle */}
      <button className="tech-overlay-toggle" onClick={toggleTechnicalOverlay} aria-label="Toggle technical overlay" onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar ? {
      borderColor: `${avatar.accent}30`,
      color: avatar.accent
    } : {}} data-id="element-10">
        {showTechnicalOverlay ? <InfoIcon size={18} data-id="element-11" /> : <MonitorIcon size={18} data-id="element-12" />}
      </button>

      {/* Fireworks controls */}
      <div className="fireworks-controls" style={avatar ? {
      borderColor: `${avatar.accent}30`
    } : {}} data-id="element-13">
        <button className={`fireworks-button ${showFireworks ? 'active' : ''}`} onClick={toggleFireworks} aria-label="Toggle fireworks" onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar && !showFireworks ? {
        color: avatar.accent,
        background: `${avatar.accent}20`
      } : {}} data-id="element-14">
          <SparklesIcon size={16} data-id="element-15" />
        </button>
        {showFireworks && <>
            <button className="fireworks-button" onClick={increaseFireworksIntensity} aria-label="Increase fireworks intensity" onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar ? {
          color: avatar.accent,
          background: `${avatar.accent}20`
        } : {}} data-id="element-16">
              <ZapIcon size={16} data-id="element-17" />
            </button>
            <div className="intensity-indicator" data-id="element-18">
              {[1, 2, 3, 4, 5, 6].map((dot, i) => <div key={i} className={`intensity-dot ${i < fireworksIntensity * 2 ? 'active' : ''}`} style={i < fireworksIntensity * 2 ? {
            background: avatar?.accent || '#6ff8f0'
          } : {}} data-id="element-19"></div>)}
            </div>
          </>}
      </div>

      {/* Audio player controls */}
      <div className="audio-player" style={avatar ? {
      borderColor: `${avatar.accent}30`
    } : {}} data-id="element-20">
        <button className="audio-button" onClick={toggleAudio} aria-label={audioPlaying ? 'Pause music' : 'Play music'} onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar ? {
        color: avatar.accent,
        background: `${avatar.accent}20`
      } : {}} data-id="element-21">
          {audioPlaying ? <PauseIcon size={16} data-id="element-22" /> : <PlayIcon size={16} data-id="element-23" />}
        </button>
        <button className="audio-button" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'} onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar ? {
        color: avatar.accent,
        background: `${avatar.accent}20`
      } : {}} data-id="element-24">
          {isMuted ? <VolumeIcon size={16} data-id="element-25" /> : <Volume2Icon size={16} data-id="element-26" />}
        </button>
        <input type="range" className="volume-slider" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} aria-label="Volume" onMouseEnter={() => handleCursorChange('button')} onMouseLeave={() => handleCursorChange('default')} style={avatar ? {
        background: `${avatar.accent}20`,
        '--webkit-slider-thumb-color': avatar.accent
      } : {}} data-id="element-27" />
      </div>

      {/* Intro Screen */}
      {showIntro && <IntroScreen onStart={handleStartExperience} onCursorChange={handleCursorChange} data-id="element-28" />}

      {/* Avatar selection modal */}
      {showAvatarSelection && !showIntro && <AvatarSelection onSelectAvatar={handleAvatarSelect} onCursorChange={handleCursorChange} data-id="element-29" />}

      {/* Avatar indicator */}
      {avatar && !showAvatarSelection && !showIntro && <AvatarIndicator onCursorChange={handleCursorChange} data-id="element-30" />}

      {/* Enhanced Loading Screen */}
      {isLoading && !showIntro && <EnhancedLoadingScreen onLoadingComplete={handleLoadingComplete} data-id="element-31" />}

      {/* Technical overlay */}
      {showTechnicalOverlay && <TechnicalOverlay data-id="element-32" />}

      {/* Custom Cursor - only show on desktop */}
      {!isMobile && <CustomCursor position={cursorPosition} cursorType={cursorType} data-id="element-33" />}

      <div className={`transition-opacity duration-1000 ${isLoading || showAvatarSelection || showIntro ? 'opacity-0' : 'opacity-100'}`} data-id="element-34">
        <Header onCursorChange={handleCursorChange} data-id="element-35" />
        <main data-id="element-36">
          <Hero onCursorChange={handleCursorChange} data-id="element-37" />
          <About onCursorChange={handleCursorChange} data-id="element-38" />
          <Disciplines onCursorChange={handleCursorChange} data-id="element-39" />
          <HallOfFame onCursorChange={handleCursorChange} data-id="element-40" />
          <Portfolio onCursorChange={handleCursorChange} data-id="element-41" />
          <Companies onCursorChange={handleCursorChange} data-id="element-42" />
          <TeamMembers onCursorChange={handleCursorChange} data-id="element-43" />
          <Shop onCursorChange={handleCursorChange} data-id="element-44" />
          <MediaFeatures onCursorChange={handleCursorChange} data-id="element-45" />
          <Contact onCursorChange={handleCursorChange} data-id="element-46" />
        </main>
        <Footer onCursorChange={handleCursorChange} data-id="element-47" />
      </div>
    </div>;
}